import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/ParentDashboard.css';
import '../assets/styles/ChoreCards.css';
import { getAuthHeaders } from '../utils/auth';

const ParentChoreManagement = ({ user, onBack }) => {
    const [chores, setChores] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [kids, setKids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, active, completed, pending_approval

    const fetchData = useCallback(async () => {
        const tenantId = user?.tenant_id;
        if (!tenantId) {
            return;
        }

        setLoading(true);
        try {
            const [choresRes, submissionsRes, kidsRes] = await Promise.all([
                axios.get(`/api/v1/chores?household_id=${tenantId}`, { headers: getAuthHeaders() }),
                axios.get(`/api/v1/submissions?household_id=${tenantId}`, { headers: getAuthHeaders() }),
                axios.get(`/api/v1/users/kids?tenant_id=${tenantId}`, { headers: getAuthHeaders() })
            ]);


            setChores(choresRes.data || []);
            setSubmissions(submissionsRes.data || []);
            const normalizedKids = (kidsRes.data || []).map(kid => ({
                ...kid,
                id: kid.ID || kid.id,
                ID: kid.ID || kid.id,
                DisplayName: kid.DisplayName || kid.display_name || kid.Username || kid.username || '',
                Username: kid.Username || kid.username || '',
                Avatar: kid.Avatar || kid.avatar || '👶'
            }));
            setKids(normalizedKids);
        } catch (err) {
            console.error('ParentChoreManagement: Failed to fetch data:', err);
            console.error('ParentChoreManagement: Error details:', err.response?.data);
        } finally {
            setLoading(false);
        }
    }, [user?.tenant_id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatCurrency = (amount) => {
        return `$${(amount / 100).toFixed(2)}`;
    };

    const getChoreStats = (choreId) => {
        const choreSubmissions = submissions.filter(s => s.challenge_id === choreId);
        return {
            total: choreSubmissions.length,
            pending: choreSubmissions.filter(s => s.status === 'pending').length,
            approved: choreSubmissions.filter(s => s.status === 'approved').length,
            rejected: choreSubmissions.filter(s => s.status === 'rejected').length
        };
    };

    const getFilteredChores = () => {
        return chores.filter(chore => {
            const stats = getChoreStats(chore.id);

            switch (filter) {
                case 'active':
                    // Active = chore is active, has no pending submissions, and hasn't been completed yet
                    // For one-time chores: exclude if they have approved submissions
                    // For recurring chores: always show if active
                    if (chore.recurrence === 'none') {
                        return chore.status === 'active' && stats.pending === 0 && stats.approved === 0;
                    }
                    return chore.status === 'active' && stats.pending === 0;
                case 'completed':
                    return stats.approved > 0;
                case 'pending_approval':
                    return stats.pending > 0;
                case 'all':
                default:
                    return true;
            }
        });
    };

    const filteredChores = getFilteredChores();

    const activeCount = chores.filter(c => {
        const stats = getChoreStats(c.id);
        // Active count: one-time chores with no approved submissions, or recurring chores with no pending
        if (c.recurrence === 'none') {
            return c.status === 'active' && stats.pending === 0 && stats.approved === 0;
        }
        return c.status === 'active' && stats.pending === 0;
    }).length;

    const pendingCount = chores.filter(c => getChoreStats(c.id).pending > 0).length;

    const completedCount = chores.filter(c => getChoreStats(c.id).approved > 0).length;

    return (
        <div className="dashboard-container">
            <header className="dashboard-header" style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                padding: '24px',
                borderRadius: '16px',
                marginBottom: '32px'
            }}>
                <div className="header-left" style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                        onClick={onBack}
                        aria-label="Back to dashboard"
                        style={{
                            background: 'white',
                            border: 'none',
                            color: '#6366f1',
                            fontSize: '1rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            marginRight: '20px',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>←</span>
                        <span>Back to Dashboard</span>
                    </button>
                    <div>
                        <h1 style={{ color: 'white', margin: 0 }}>Chore Management</h1>
                        <p style={{ color: 'rgba(255,255,255,0.9)', margin: '4px 0 0 0', fontSize: '0.95rem' }}>
                            Manage all your household chores
                        </p>
                    </div>
                </div>
            </header>

            <div className="stats-bar" style={{ marginBottom: '24px' }}>
                <div className="stat-item">
                    <div className="stat-label">Total Chores 📋</div>
                    <div className="stat-value">{chores.length}</div>
                </div>
                <div className="stat-item highlight-card">
                    <div className="stat-label">Active ✅</div>
                    <div className="stat-value">{activeCount}</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">Pending Approval ⏳</div>
                    <div className="stat-value">{pendingCount}</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">Completed 🎉</div>
                    <div className="stat-value">{completedCount}</div>
                </div>
            </div>

            <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }} role="tablist" aria-label="Chore filter tabs">
                <button
                    onClick={() => setFilter('all')}
                    role="tab"
                    aria-selected={filter === 'all'}
                    aria-label={`All chores filter, ${chores.length} chores`}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filter === 'all' ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : '#e5e7eb',
                        color: filter === 'all' ? 'white' : '#374151',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    All ({chores.length})
                </button>
                <button
                    onClick={() => setFilter('active')}
                    role="tab"
                    aria-selected={filter === 'active'}
                    aria-label={`Active chores filter, ${activeCount} chores`}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filter === 'active' ? '#10b981' : '#e5e7eb',
                        color: filter === 'active' ? 'white' : '#374151',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Active ({activeCount})
                </button>
                <button
                    onClick={() => setFilter('pending_approval')}
                    role="tab"
                    aria-selected={filter === 'pending_approval'}
                    aria-label={`Pending approval filter, ${pendingCount} chores`}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filter === 'pending_approval' ? '#f59e0b' : '#e5e7eb',
                        color: filter === 'pending_approval' ? 'white' : '#374151',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Pending Approval ({pendingCount})
                </button>
                <button
                    onClick={() => setFilter('completed')}
                    role="tab"
                    aria-selected={filter === 'completed'}
                    aria-label={`Completed chores filter, ${completedCount} chores`}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filter === 'completed' ? '#8b5cf6' : '#e5e7eb',
                        color: filter === 'completed' ? 'white' : '#374151',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Completed ({completedCount})
                </button>
            </div>

            {loading ? (
                <div className="loading">Loading chores...</div>
            ) : filteredChores.length === 0 ? (
                <div className="onboarding-card">
                    <span className="large-emoji">📋</span>
                    <h2>No Chores Found</h2>
                    <p>No chores match the selected filter.</p>
                    <div style={{ marginTop: '20px', padding: '10px', background: '#f3f4f6', borderRadius: '8px', fontSize: '0.875rem', textAlign: 'left' }}>
                        <strong>Debug Info:</strong>
                        <div>User tenant_id: {user?.tenant_id || 'MISSING'}</div>
                        <div>Total chores in database: {chores.length}</div>
                        <div>Total submissions: {submissions.length}</div>
                        <div>Total kids: {kids.length}</div>
                        <div>Current filter: {filter}</div>
                        <div>Filtered chores: {filteredChores.length}</div>
                        <div style={{ marginTop: '10px', padding: '8px', background: '#fef3c7', borderRadius: '4px' }}>
                            <strong>⚠️ Troubleshooting:</strong>
                            <div style={{ marginTop: '4px' }}>
                                {chores.length === 0 && submissions.length === 0 && kids.length === 0 ? (
                                    <>
                                        <div>• All API calls returned empty arrays</div>
                                        <div>• Check browser console for API errors</div>
                                        <div>• Verify you have created chores in the system</div>
                                        <div>• Check if tenant_id is correct</div>
                                    </>
                                ) : (
                                    <div>Some data was fetched successfully</div>
                                )}
                            </div>
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '0.75rem', color: '#6b7280' }}>
                            <strong>API Endpoints being called:</strong>
                            <div>• GET /api/v1/chores?household_id={user?.tenant_id}</div>
                            <div>• GET /api/v1/submissions?household_id={user?.tenant_id}</div>
                            <div>• GET /api/v1/users/kids?tenant_id={user?.tenant_id}</div>
                        </div>
                        <div style={{ marginTop: '10px', padding: '8px', background: '#dbeafe', borderRadius: '4px', fontSize: '0.75rem' }}>
                            <strong>💡 To see chores here:</strong>
                            <div>1. Click "← Back to Dashboard"</div>
                            <div>2. Click "Create New Chore" action card</div>
                            <div>3. Fill in chore details and assign to a kid</div>
                            <div>4. Return here to see it appear</div>
                            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>
                                Check browser console (F12) for "ParentChoreManagement:" logs to see API responses
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {filteredChores.map(chore => {
                        const stats = getChoreStats(chore.id);
                        const choreSubmissions = submissions.filter(s => s.challenge_id === chore.id);

                        return (
                            <div
                                key={chore.id}
                                className="chore-card"
                                style={{
                                    borderLeft: `4px solid ${stats.pending > 0 ? '#f59e0b' :
                                        stats.approved > 0 ? '#10b981' :
                                            '#6366f1'
                                        }`
                                }}
                            >
                                <div className="chore-header">
                                    <div style={{ flex: 1 }}>
                                        <h3>{chore.title}</h3>
                                        {chore.description && (
                                            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>
                                                {chore.description}
                                            </p>
                                        )}
                                        <div style={{
                                            flexWrap: 'wrap',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{
                                                fontSize: '0.75rem',
                                                padding: '4px 8px',
                                                background: '#f3f4f6',
                                                borderRadius: '4px',
                                                color: '#374151'
                                            }}>
                                                {chore.recurrence === 'none' ? 'One-time' : `Recurring: ${chore.recurrence}`}
                                            </span>

                                            {/* Nudge Button for Active Chores */}
                                            {chore.status === 'active' && stats.pending === 0 && stats.approved === 0 && (
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            // We need kid_id to nudge. If chore has specific assignees, nudge them.
                                                            // For now, household-wide chores might annoy everyone, but let's assume specific assignment or nudge all.
                                                            // Actually, let's just alert for now or call the endpoint if we have a kid ID.
                                                            // Since we don't have a specific kid context here (it's a list of chores), 
                                                            // we might need to nudge all assignees.
                                                            // Let's iterate assignees if available.
                                                            // We need chore.assignees from the API response.
                                                            // Assuming GET /chores returns assignees.
                                                            // If not, we can't specific target.
                                                            // Simple implementation:
                                                            alert('Nudge sent! 🔔');
                                                            await axios.post('/api/v1/notifications/nudge', {
                                                                chore_id: chore.id
                                                            }, { headers: getAuthHeaders() });
                                                        } catch (err) {
                                                            console.error(err);
                                                        }
                                                    }}
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        padding: '4px 8px',
                                                        background: '#fff',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    🔔 Nudge
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <span style={{
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                padding: '4px 8px',
                                                background: stats.approved > 0 ? '#dbeafe' : (stats.pending > 0 ? '#fef3c7' : '#d1fae5'),
                                                borderRadius: '4px',
                                                color: stats.approved > 0 ? '#1e40af' : (stats.pending > 0 ? '#92400e' : '#065f46')
                                            }}>
                                                {stats.approved > 0 ? 'completed' : (stats.pending > 0 ? 'pending' : 'active')}
                                            </span>
                                        </div>
                                        <span className="chore-reward">{formatCurrency(chore.reward_amount)}</span>
                                    </div>
                                </div>

                                {/* Submission Stats */}
                                {stats.total > 0 && (
                                    <div style={{
                                        marginTop: '16px',
                                        padding: '12px',
                                        background: '#f9fafb',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        gap: '16px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600' }}>
                                                Total Submissions
                                            </div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                                                {stats.total}
                                            </div>
                                        </div>
                                        {stats.pending > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: '600' }}>
                                                    ⏳ Pending
                                                </div>
                                                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f59e0b' }}>
                                                    {stats.pending}
                                                </div>
                                            </div>
                                        )}
                                        {stats.approved > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '600' }}>
                                                    ✅ Approved
                                                </div>
                                                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10b981' }}>
                                                    {stats.approved}
                                                </div>
                                            </div>
                                        )}
                                        {stats.rejected > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '600' }}>
                                                    ❌ Rejected
                                                </div>
                                                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444' }}>
                                                    {stats.rejected}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Recent Submissions */}
                                {choreSubmissions.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                            Recent Submissions:
                                        </div>
                                        {choreSubmissions.slice(0, 3).map(sub => {
                                            const kid = kids.find(k => k.ID === sub.kid_user_id);
                                            return (
                                                <div
                                                    key={sub.id}
                                                    style={{
                                                        fontSize: '0.875rem',
                                                        color: '#6b7280',
                                                        padding: '8px',
                                                        background: 'white',
                                                        borderRadius: '4px',
                                                        marginBottom: '4px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <span>
                                                        {kid?.Username || 'Unknown'} - {new Date(sub.submitted_at).toLocaleDateString()}
                                                    </span>
                                                    <span style={{
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600',
                                                        background: sub.status === 'approved' ? '#d1fae5' :
                                                            sub.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                                                        color: sub.status === 'approved' ? '#065f46' :
                                                            sub.status === 'rejected' ? '#991b1b' : '#92400e'
                                                    }}>
                                                        {sub.status}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ParentChoreManagement;
